'use client';
import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

type DemoState = 'idle' | 'transforming' | 'done';

interface ROLine {
  desc: string;
  qty: string;
  rate: string;
  total: string;
  type: 'labor' | 'parts' | 'misc';
}

const rawLines: ROLine[] = [
  { desc: 'Oil change - 5W-30 synthetic', qty: '1', rate: '$38.00', total: '$38.00', type: 'misc' },
  { desc: 'Oil filter', qty: '1', rate: '$12.00', total: '$12.00', type: 'parts' },
  { desc: 'Front brake pads (OEM)', qty: '2', rate: '$64.00', total: '$128.00', type: 'parts' },
  { desc: 'Brake rotor resurface', qty: '2', rate: '$45.00', total: '$90.00', type: 'labor' },
  { desc: 'Labor - brake inspection', qty: '1.5h', rate: '$110.00', total: '$165.00', type: 'labor' },
  { desc: 'Tire rotation', qty: '1', rate: '$25.00', total: '$25.00', type: 'labor' },
];

const invoiceLines: ROLine[] = [
  { desc: 'Engine oil service (5W-30 synthetic)', qty: '1', rate: '$38.00', total: '$38.00', type: 'misc' },
  { desc: 'Oil filter — OEM spec', qty: '1', rate: '$15.60', total: '$15.60', type: 'parts' },
  { desc: 'Front brake pads — OEM (×2)', qty: '2', rate: '$83.20', total: '$166.40', type: 'parts' },
  { desc: 'Rotor resurface (×2)', qty: '2', rate: '$45.00', total: '$90.00', type: 'labor' },
  { desc: 'Brake system inspection — 1.5h', qty: '1.5h', rate: '$110.00', total: '$165.00', type: 'labor' },
  { desc: 'Tire rotation & torque check', qty: '1', rate: '$25.00', total: '$25.00', type: 'labor' },
  { desc: 'Parts markup (30% matrix)', qty: '', rate: '', total: '+$55.60', type: 'parts' },
  { desc: 'Sales tax (8.25%)', qty: '', rate: '', total: '+$46.90', type: 'misc' },
];

const InteractiveDemo: React.FC = () => {
  const [demoState, setDemoState] = useState<DemoState>('idle');
  const [visibleLines, setVisibleLines] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const runDemo = () => {
    if (demoState !== 'idle') return;
    setDemoState('transforming');
    setVisibleLines(0);
    // Reveal invoice lines one by one
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setVisibleLines(i);
      if (i >= invoiceLines.length) {
        clearInterval(interval);
        setTimeout(() => setDemoState('done'), 300);
      }
    }, 160);
  };

  const reset = () => {
    setDemoState('idle');
    setVisibleLines(0);
  };

  const totalRO = rawLines.reduce((acc, l) => acc + parseFloat(l.total.replace('$', '')), 0);
  const totalInvoice = 602.50; // pre-calculated with markup + tax

  return (
    <section id="demo" className="py-24 px-6">
      <div className="max-w-5xl mx-auto" ref={ref}>
        {/* Label */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[11px] tracking-[0.4em] uppercase text-[rgba(192,199,208,0.5)] font-medium">
            Live Demo
          </span>
          <h2 className="heading font-black text-white mt-3" style={{ fontSize: 'clamp(1.8rem, 4.5vw, 3rem)' }}>
            Watch a repair order become an invoice
          </h2>
          <p className="text-[#C0C7D0] mt-3 text-base max-w-xl mx-auto">
            A real 2019 Ford F-150 service ticket. Tap the button. Watch Invoice apply markup, calculate tax, and format the final invoice — in real time.
          </p>
        </div>

        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-6 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* LEFT: Raw RO */}
          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[rgba(192,199,208,0.5)] mb-1">Repair Order</div>
                <div className="heading font-bold text-white text-base">RO #20847 — 2019 Ford F-150</div>
              </div>
              <span className="glass-panel px-3 py-1.5 text-[11px] text-[#C0C7D0] tracking-wide">Handwritten</span>
            </div>

            <div className="space-y-2 mb-5">
              {rawLines.map((line, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between py-2.5 px-3 rounded-lg ${
                    demoState !== 'idle' ? 'demo-strike opacity-50' : ''
                  }`}
                  style={demoState !== 'idle' ? { animationDelay: `${i * 80}ms` } : {}}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        line.type === 'labor' ? 'bg-[#C0C7D0]' : line.type === 'parts' ? 'bg-[rgba(192,199,208,0.5)]' : 'bg-[rgba(192,199,208,0.3)]'
                      }`}
                    />
                    <span className="text-[12px] text-[#C0C7D0] truncate">{line.desc}</span>
                  </div>
                  <span className="mono text-[12px] text-[rgba(192,199,208,0.7)] ml-4 flex-shrink-0">{line.total}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-[rgba(255,255,255,0.08)] pt-4 flex justify-between">
              <span className="text-[#C0C7D0] text-sm">Subtotal (no markup)</span>
              <span className="mono font-bold text-[#C0C7D0]">${totalRO.toFixed(2)}</span>
            </div>
            <div className="mt-2 text-[11px] text-[rgba(192,199,208,0.35)]">
              ⚠ Parts markup not applied · Tax not calculated · Not sent
            </div>
          </div>

          {/* RIGHT: Invoice output */}
          <div className={`glass-panel p-6 border-[rgba(0,229,204,0.2)] ${demoState === 'done' ? 'teal-glow-box' : ''} transition-all duration-500`}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-teal mb-1">Invoice Output</div>
                <div className="heading font-bold text-white text-base">INV-20847 · Formatted & Ready</div>
              </div>
              {demoState === 'done' && (
                <span className="flex items-center gap-1.5 bg-[rgba(0,229,204,0.15)] text-teal px-3 py-1.5 rounded-full text-[11px] font-semibold">
                  <Icon name="PaperAirplaneIcon" size={12} className="text-teal" />
                  Sent via SMS
                </span>
              )}
              {demoState === 'idle' && (
                <span className="glass-panel px-3 py-1.5 text-[11px] text-[rgba(192,199,208,0.4)] tracking-wide">Waiting...</span>
              )}
            </div>

            <div className="space-y-2 mb-5 min-h-[220px]">
              {demoState === 'idle' && (
                <div className="flex flex-col items-center justify-center h-48 text-[rgba(192,199,208,0.25)]">
                  <Icon name="DocumentTextIcon" size={40} className="text-[rgba(192,199,208,0.15)] mb-3" />
                  <span className="text-sm">Press the button to transform this RO</span>
                </div>
              )}
              {demoState !== 'idle' &&
                invoiceLines.slice(0, visibleLines).map((line, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between py-2.5 px-3 rounded-lg demo-line-appear ${
                      line.desc.includes('markup') || line.desc.includes('tax')
                        ? 'bg-[rgba(0,229,204,0.06)] border border-[rgba(0,229,204,0.15)]' :''
                    }`}
                    style={{ animationDelay: '0ms' }}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span
                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          line.type === 'labor' ? 'bg-teal' : line.type === 'parts' ? 'bg-[rgba(0,229,204,0.6)]' : 'bg-[rgba(0,229,204,0.4)]'
                        }`}
                      />
                      <span className="text-[12px] text-white truncate">{line.desc}</span>
                    </div>
                    <span className={`mono text-[12px] font-semibold ml-4 flex-shrink-0 ${line.total.startsWith('+') ? 'text-teal' : 'text-[#C0C7D0]'}`}>
                      {line.total}
                    </span>
                  </div>
                ))}
            </div>

            {demoState === 'done' && (
              <div className="border-t border-[rgba(0,229,204,0.2)] pt-4">
                <div className="flex justify-between mb-1">
                  <span className="text-[#C0C7D0] text-sm">Invoice Total</span>
                  <span className="mono font-bold text-teal text-lg">${totalInvoice.toFixed(2)}</span>
                </div>
                <div className="text-[11px] text-teal opacity-70">
                  ✓ Markup applied · ✓ Tax calculated · ✓ PDF generated · ✓ SMS sent in 4.2s
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Demo CTA */}
        <div className={`mt-8 text-center transition-all duration-700 delay-400 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          {demoState === 'idle' && (
            <button onClick={runDemo} className="btn-teal px-10 py-4 text-[15px]">
              Transform This Repair Order →
            </button>
          )}
          {demoState === 'transforming' && (
            <div className="flex items-center justify-center gap-3 text-teal">
              <span className="w-2 h-2 rounded-full bg-teal teal-pulse" />
              <span className="mono text-sm">Building invoice...</span>
            </div>
          )}
          {demoState === 'done' && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="#" className="btn-teal px-10 py-4 text-[15px]">
                Start Free — No Card, No DMS Migration
              </a>
              <button onClick={reset} className="btn-ghost px-6 py-4 text-[13px]">
                Run Again
              </button>
            </div>
          )}
          {demoState === 'done' && (
            <p className="text-[11px] text-[rgba(192,199,208,0.35)] mt-3">
              That&apos;s $144.50 more than the handwritten ticket — recovered automatically.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default InteractiveDemo;